import { useMemo } from "react";
import { AlertTriangle, RotateCcw, Sparkles, Zap, LayoutPanelLeft, Grid3x3, Droplet, Square, PaintRoller, DoorOpen, ShowerHead, Layers, Hammer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { UMBRAL_BAJO, PRECIO_MEDIA } from "@/data/pro-partidas";

const ICONS = { Hammer, LayoutPanelLeft, Zap, Droplet, Grid3x3, Square, PaintRoller, DoorOpen, ShowerHead, Layers };

function eur(n) { return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(n || 0)); }

export default function PartidasTable({ partidas, surface, onChange, onApplyOptimized, onResetAll }) {
  const totals = useMemo(() => {
    const incluidas = partidas.filter(p => p.incluir);
    const subtotal = incluidas.reduce((s, p) => s + p.precioFinal * surface, 0);
    const ahorro = incluidas.reduce((s, p) => s + Math.max(0, p.estandar - p.precioFinal) * surface, 0);
    return { subtotal, ahorro };
  }, [partidas, surface]);

  const update = (id, patch) => {
    onChange(partidas.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  const setModo = (p, modo) => {
    if (modo === 'estandar') update(p.id, { modo, precioFinal: p.estandar });
    else if (modo === 'optimizado') update(p.id, { modo, precioFinal: p.optimizado });
    else update(p.id, { modo: 'personalizado' });
  };

  const setCustomPrice = (p, value) => {
    const v = parseFloat(value);
    update(p.id, { modo: 'personalizado', precioFinal: isNaN(v) ? 0 : v });
  };

  return (
    <div className="rounded-2xl border border-slate-200 dark-border bg-white dark-bg-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-4 border-b border-slate-100 dark-border">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 dark-text-mid font-semibold">Partidas de la reforma</div>
          <div className="font-display font-semibold text-slate-900 dark-text-strong text-lg leading-tight">Edita cada partida según tu cliente</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onApplyOptimized}
            data-testid="apply-global-optimized-btn"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Versión optimizada global</span>
            <span className="sm:hidden">Optimizar</span>
          </button>
          <button
            type="button"
            onClick={onResetAll}
            data-testid="reset-all-btn"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 dark-border text-slate-600 dark-text-mid text-xs font-medium hover:border-slate-300 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset
          </button>
        </div>
      </div>

      <div className="divide-y divide-slate-100 dark-border">
        {partidas.map(p => {
          const Icon = ICONS[p.icon] || Hammer;
          const importe = p.precioFinal * surface;
          const ahorroPct = p.estandar > 0 ? Math.max(0, (1 - p.precioFinal / p.estandar) * 100) : 0;
          const belowAvg = p.modo === 'personalizado' && p.precioFinal < UMBRAL_BAJO(p);
          const belowMedia = p.modo === 'personalizado' && p.precioFinal < PRECIO_MEDIA(p) && !belowAvg;
          const fade = !p.incluir;

          return (
            <div
              key={p.id}
              data-testid={`partida-row-${p.id}`}
              className={"px-5 md:px-6 py-4 transition-colors " + (fade ? "opacity-50" : "")}
            >
              <div className="grid grid-cols-12 gap-3 md:gap-4 items-center">
                {/* Toggle + Nombre */}
                <div className="col-span-12 md:col-span-4 flex items-start gap-3">
                  <Switch
                    checked={p.incluir}
                    onCheckedChange={(v) => update(p.id, { incluir: v })}
                    data-testid={`partida-toggle-${p.id}`}
                    className="data-[state=checked]:bg-emerald-600 mt-1"
                  />
                  <div className="w-10 h-10 rounded-lg bg-slate-100 dark-bg-pill text-slate-700 dark-text-mid grid place-items-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-semibold text-slate-900 dark-text-strong text-sm leading-tight">{p.name}</div>
                      {p.ajustable && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 dark-bg-pill text-emerald-700 dark-text-accent font-semibold">Ajustable</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 dark-text-mid mt-0.5 leading-snug">{p.descripcion}</div>
                  </div>
                </div>

                {/* Modo */}
                <div className="col-span-6 md:col-span-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 dark-text-mid font-semibold mb-1.5">Modo</div>
                  <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 dark-bg-pill p-1">
                    {[
                      { id: 'estandar', label: 'Estándar' },
                      { id: 'optimizado', label: 'Opt.' },
                      { id: 'personalizado', label: 'Manual' },
                    ].map(opt => {
                      const active = p.modo === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setModo(p, opt.id)}
                          data-testid={`partida-mode-${p.id}-${opt.id}`}
                          className={[
                            "text-[11px] font-medium h-7 rounded-md transition-all",
                            active ? "bg-white dark-bg-card text-emerald-700 dark-text-accent shadow-sm" : "text-slate-600 dark-text-mid hover:text-slate-900"
                          ].join(" ")}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Precio */}
                <div className="col-span-3 md:col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 dark-text-mid font-semibold mb-1.5">€/m²</div>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={p.precioFinal}
                      onChange={(e) => setCustomPrice(p, e.target.value)}
                      data-testid={`partida-price-${p.id}`}
                      className="h-9 text-sm font-display font-semibold tabular-nums focus-visible:ring-emerald-500 focus-visible:ring-offset-0 dark-bg-card dark-text-strong dark-border"
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[10px] text-slate-400 dark-text-mid">
                    <span>est {p.estandar}</span>
                    <span className="text-emerald-600">opt {p.optimizado}</span>
                  </div>
                </div>

                {/* Importe */}
                <div className="col-span-3 md:col-span-3 text-right">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 dark-text-mid font-semibold mb-1.5">Importe</div>
                  <div className="font-display font-bold text-slate-900 dark-text-strong text-lg tabular-nums">{eur(importe)}</div>
                  {ahorroPct > 0 && (
                    <div className="text-[10px] text-emerald-600 font-semibold">−{ahorroPct.toFixed(0)}% vs estándar</div>
                  )}
                </div>
              </div>

              {/* Aviso por debajo de la media */}
              {p.incluir && (belowAvg || belowMedia) && (
                <div
                  data-testid={`partida-warn-${p.id}`}
                  className={[
                    "mt-3 flex items-start gap-2 rounded-lg p-3 text-xs leading-relaxed",
                    belowAvg ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-slate-50 dark-bg-pill border border-slate-200 dark-border text-slate-700 dark-text-mid"
                  ].join(" ")}
                >
                  <AlertTriangle className={"w-4 h-4 shrink-0 mt-0.5 " + (belowAvg ? "text-amber-600" : "text-slate-500")} />
                  <div>
                    {belowAvg
                      ? <>Precio <strong>muy por debajo</strong> de la media de mercado. ¿Estás seguro de que cubre materiales y mano de obra de calidad?</>
                      : <>Precio por debajo de la media de mercado. Revisa que sigas cubriendo materiales y mano de obra.</>
                    }
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="px-5 md:px-6 py-4 border-t border-slate-100 dark-border bg-slate-50/60 dark-bg-section flex items-center justify-between">
        <div className="text-xs text-slate-500 dark-text-mid">
          Ahorro acumulado: <strong className="text-emerald-600">{eur(totals.ahorro)}</strong>
        </div>
        <div className="text-sm font-display font-semibold text-slate-900 dark-text-strong tabular-nums" data-testid="partidas-subtotal">
          Subtotal: {eur(totals.subtotal)}
        </div>
      </div>
    </div>
  );
}
